import { later } from '@ember/runloop';

import { modifier } from 'ember-modifier';

import { getWidthWithoutPaddings } from 'webapp/utils/html';

const CLASS_NAMES = {
	BOTTOM_ACTIONS_HOVER: 'grid-item-bottom-actions--hover',
	HAS_STRETCHABLE_ITEMS: 'has-stretchable-items',
	RESIZER_HOVER_RIGHT: 'grid-item-resizer--hover-right',
	RESIZER_HOVER_LEFT: 'grid-item-resizer--hover-left',
	LAST_COLUMN: 'grid-item--last-column-in-row',
	STRETCHABLE: 'stretchable',
	ADD_FRAGMENT_BUTTON_ACTIVE: 'add-fragment-button--active',
	ADD_FRAGMENT_WRAPPER_VISIBLE: 'add-fragment-wrapper--visible',
	RESIZE_HANDLE_DISABLED: 'resize-handle--disabled',
	RESIZE_HANDLE_HOVER: 'resize-handle--hover',
	RESIZE_HANDLE_VERTICAL_DISABLED: 'resize-handle-vertical--disabled',
	RESIZE_HANDLE_VERTICAL_HOVER: 'resize-handle-vertical--hover',
} as const;

const THRESHOLD = 0.1;
const THRESHOLD_BUFFER = 20;
const HOVER_SHOW_DELAY = 300; // ms - delay before showing resize-handles and buttons
const HOVER_HIDE_DELAY = 100; // ms - delay before hiding resize-handles and buttons

interface ModifierArgs {
	isEditable?: boolean;
	isPageless?: boolean;
	row: number;
	column: number;
}

const reportEditorGridItemInteractions = (
	element: HTMLElement,
	_positionalArgs: [],
	{ isEditable, isPageless, row, column }: ModifierArgs,
) => {
	const resizerNode = element.querySelector('.grid-item-resizer'),
		containerSelector = isPageless ? '.pageless-content' : '.usercontent',
		containerNode = element.closest<HTMLElement>(containerSelector)!;

	if (!containerNode || !isEditable) {
		return;
	}

	// query rowItems dynamically each time to ensure we always have the current set.
	// items in the same row may render or become visible at different times (e.g. lazy loading),
	// so a static snapshot captured at setup time can become stale.
	const getRowItems = () => containerNode.querySelectorAll(`.grid-item[data-row="${row}"]`);

	let showResizerTimeout: number | null = null;
	let showPreviousResizerTimeout: number | null = null;
	let showLeftWrapperTimeout: number | null = null;
	let showBottomActionsTimeout: number | null = null;

	const handleBottomActionsVisibility = (event: MouseEvent) => {
		if (!(event.target instanceof HTMLElement)) {
			return;
		}

		const isInAnyBottomThresholdArea = isMouseInAnyBottomThreshold(event);

		const rowItems = getRowItems();

		// get bottom actions container of first item in the row
		const firstColumnItem = Array.from(rowItems).find(
				(item) => item.getAttribute('data-column') === '1',
			),
			bottomActions = firstColumnItem?.querySelector<HTMLElement>(
				'.grid-item-bottom-actions',
			),
			bottomActionsAddButtonWrapper = bottomActions?.querySelector<HTMLElement>(
				'.add-fragment-button-wrapper',
			),
			resizeHandleVertical = bottomActions?.querySelector('.resize-handle-vertical');

		if (!bottomActions) {
			return;
		}

		// find first stretchable item in the row
		const stretchableItem = Array.from(rowItems).find((item) =>
			item.classList.contains(CLASS_NAMES.STRETCHABLE),
		);

		// check if popup is open by checking if add button is active
		const addButton = bottomActions.querySelector('.add-fragment-button')!,
			isPopupOpen = addButton?.classList.contains(CLASS_NAMES.ADD_FRAGMENT_BUTTON_ACTIVE);

		// apply or schedule hover class for bottom actions
		if (isPopupOpen || isInAnyBottomThresholdArea) {
			const hasHoverClass = bottomActions.classList.contains(
				CLASS_NAMES.BOTTOM_ACTIONS_HOVER,
			);

			const handleBottomActionsClasses = () => {
				// handle bottom actions and button visibility
				// button is separate from bottom actions because we also have to show it when popup is open
				bottomActions.classList.add(CLASS_NAMES.BOTTOM_ACTIONS_HOVER);
				bottomActions.classList.toggle(
					CLASS_NAMES.HAS_STRETCHABLE_ITEMS,
					!!stretchableItem,
				);
				bottomActionsAddButtonWrapper?.classList.add(
					CLASS_NAMES.ADD_FRAGMENT_WRAPPER_VISIBLE,
				);

				// center full width vertical resize-handle of first item in the row
				const availableWidth = isPageless
					? getWidthWithoutPaddings(containerNode)
					: containerNode.clientWidth;
				const leftOffset = availableWidth / 2;

				if (bottomActions) {
					Object.assign(bottomActions.style, {
						left: `${leftOffset}px`,
						width: `${availableWidth}px`,
						transform: 'translateX(-50%)',
					});
				}

				// show resize handle and set disabled state
				if (resizeHandleVertical) {
					const isDisabled = !stretchableItem;
					resizeHandleVertical.classList.toggle(
						CLASS_NAMES.RESIZE_HANDLE_VERTICAL_DISABLED,
						isDisabled,
					);

					// don't show handle in edge case when popup is open, just show the button
					if (!isPopupOpen) {
						resizeHandleVertical.classList.add(
							CLASS_NAMES.RESIZE_HANDLE_VERTICAL_HOVER,
						);
					}
				}
			};

			// if already has class or popup is open, apply immediately, otherwise delay
			if (hasHoverClass || isPopupOpen) {
				handleBottomActionsClasses();
			} else if (!showBottomActionsTimeout) {
				showBottomActionsTimeout = window.setTimeout(() => {
					handleBottomActionsClasses();
					showBottomActionsTimeout = null;
				}, HOVER_SHOW_DELAY);
			}
		} else {
			// cancel show timeout if in progress
			if (showBottomActionsTimeout) {
				window.clearTimeout(showBottomActionsTimeout);
				showBottomActionsTimeout = null;
			}

			if (
				bottomActions.classList.contains(CLASS_NAMES.BOTTOM_ACTIONS_HOVER) &&
				!isPopupOpen
			) {
				// hide bottom actions, resize-handle and button
				bottomActions.classList.remove(CLASS_NAMES.BOTTOM_ACTIONS_HOVER);
				resizeHandleVertical?.classList.remove(CLASS_NAMES.RESIZE_HANDLE_VERTICAL_HOVER);
				bottomActionsAddButtonWrapper?.classList.remove(
					CLASS_NAMES.ADD_FRAGMENT_WRAPPER_VISIBLE,
				);
			}
		}
	};

	const isMouseInAnyBottomThreshold = (event: MouseEvent) => {
		// check if mouse is directly over any vertical resize handle
		if (event.target instanceof HTMLElement) {
			const isOverResizeHandle =
				event.target.classList.contains('resize-handle-vertical') ||
				event.target.closest('.resize-handle-vertical') !== null;

			if (isOverResizeHandle) {
				return true;
			}

			// check if mouse is over bottom actions
			const isOverBottomActions =
				event.target.classList.contains('grid-item-bottom-actions') ||
				event.target.closest('.grid-item-bottom-actions') !== null;

			if (isOverBottomActions) {
				return true;
			}
		}

		// check if mouse is in the bottom threshold area of any item
		return Array.from(getRowItems()).some((item) => {
			const itemRect = item.getBoundingClientRect();
			const bottomThreshold = itemRect.bottom - itemRect.height * THRESHOLD;

			const isInVerticalRange =
				event.clientY >= bottomThreshold - THRESHOLD_BUFFER &&
				event.clientY <= itemRect.bottom;

			const isInHorizontalRange =
				event.clientX >= itemRect.left && event.clientX <= itemRect.right;

			return isInVerticalRange && isInHorizontalRange;
		});
	};

	const handleHorizontalResizersVisibility = (event: MouseEvent) => {
		if (!(event.target instanceof HTMLElement)) {
			return;
		}

		// get current item rect and mouse position
		const currentRect = element.getBoundingClientRect(),
			mouseX = event.clientX,
			rightThreshold = currentRect.right - currentRect.width * THRESHOLD,
			leftThreshold = currentRect.left + currentRect.width * THRESHOLD;

		const isInRightThreshold =
				mouseX >= rightThreshold - THRESHOLD_BUFFER &&
				mouseX <= currentRect.right + THRESHOLD_BUFFER,
			isInLeftThreshold =
				mouseX <= leftThreshold + THRESHOLD_BUFFER &&
				mouseX >= currentRect.left - THRESHOLD_BUFFER;

		const isDisabled = element.classList.contains(CLASS_NAMES.LAST_COLUMN);

		//--- BEGIN handle current item resizer ---//
		if (resizerNode) {
			const resizerAddButtonWrapper = resizerNode.querySelector('.add-fragment-wrapper'),
				resizeHandle = resizerNode.querySelector('.resize-handle');

			resizeHandle?.classList.toggle(CLASS_NAMES.RESIZE_HANDLE_DISABLED, isDisabled);

			// apply or schedule hover class
			if (isInRightThreshold) {
				const hasHoverLeftClass = resizerNode.classList.contains(
					CLASS_NAMES.RESIZER_HOVER_LEFT,
				);

				// apply or schedule hover class
				if (hasHoverLeftClass) {
					resizerNode.classList.add(CLASS_NAMES.RESIZER_HOVER_RIGHT);
					resizeHandle?.classList.add(CLASS_NAMES.RESIZE_HANDLE_HOVER);
					resizerNode.classList.remove(CLASS_NAMES.RESIZER_HOVER_LEFT);
					resizerAddButtonWrapper?.classList.add(
						CLASS_NAMES.ADD_FRAGMENT_WRAPPER_VISIBLE,
					);
				} else if (!showResizerTimeout) {
					// add the class with delay
					showResizerTimeout = window.setTimeout(() => {
						resizerNode.classList.add(CLASS_NAMES.RESIZER_HOVER_RIGHT);
						resizeHandle?.classList.add(CLASS_NAMES.RESIZE_HANDLE_HOVER);
						resizerAddButtonWrapper?.classList.add(
							CLASS_NAMES.ADD_FRAGMENT_WRAPPER_VISIBLE,
						);

						showResizerTimeout = null;
					}, HOVER_SHOW_DELAY);
				}
			} else {
				// schedule removal of hover class with delay
				if (showResizerTimeout) {
					window.clearTimeout(showResizerTimeout);
					showResizerTimeout = null;
				}

				if (resizerNode.classList.contains(CLASS_NAMES.RESIZER_HOVER_RIGHT)) {
					resizerNode.classList.remove(CLASS_NAMES.RESIZER_HOVER_RIGHT);
					resizeHandle?.classList.remove(CLASS_NAMES.RESIZE_HANDLE_HOVER);
					resizerAddButtonWrapper?.classList.remove(
						CLASS_NAMES.ADD_FRAGMENT_WRAPPER_VISIBLE,
					);
				}
			}
		}
		//--- END handle current item resizer ---//

		//--- BEGIN handle previous item resizer ---//
		const previousItem = Array.from(getRowItems()).find(
			(item) => parseInt(item.getAttribute('data-column') || '0') === column - 1,
		);

		if (previousItem) {
			const previousResizer = previousItem.querySelector('.grid-item-resizer');
			if (previousResizer) {
				const previousResizerAddButtonWrappers =
						previousResizer.querySelectorAll('.add-fragment-wrapper'),
					resizeHandle = previousResizer.querySelector('.resize-handle');

				const isDisabled = column === 1;
				resizeHandle?.classList.toggle(CLASS_NAMES.RESIZE_HANDLE_DISABLED, isDisabled);

				// apply or schedule hover class
				if (isInLeftThreshold) {
					const hasHoverRightClass = previousResizer.classList.contains(
						CLASS_NAMES.RESIZER_HOVER_RIGHT,
					);

					if (hasHoverRightClass) {
						previousResizer.classList.add(CLASS_NAMES.RESIZER_HOVER_LEFT);
						resizeHandle?.classList.add(CLASS_NAMES.RESIZE_HANDLE_HOVER);
						previousResizer.classList.remove(CLASS_NAMES.RESIZER_HOVER_RIGHT);
						previousResizerAddButtonWrappers.forEach((wrapper) => {
							wrapper.classList.add(CLASS_NAMES.ADD_FRAGMENT_WRAPPER_VISIBLE);
						});
					} else if (!showPreviousResizerTimeout) {
						showPreviousResizerTimeout = window.setTimeout(() => {
							previousResizer.classList.add(CLASS_NAMES.RESIZER_HOVER_LEFT);
							resizeHandle?.classList.add(CLASS_NAMES.RESIZE_HANDLE_HOVER);
							previousResizerAddButtonWrappers.forEach((wrapper) => {
								wrapper.classList.add(CLASS_NAMES.ADD_FRAGMENT_WRAPPER_VISIBLE);
							});

							showPreviousResizerTimeout = null;
						}, HOVER_SHOW_DELAY);
					}
				} else {
					// remove hover classes
					if (showPreviousResizerTimeout) {
						window.clearTimeout(showPreviousResizerTimeout);
						showPreviousResizerTimeout = null;
					}

					if (previousResizer.classList.contains(CLASS_NAMES.RESIZER_HOVER_LEFT)) {
						previousResizer.classList.remove(CLASS_NAMES.RESIZER_HOVER_LEFT);
						resizeHandle?.classList.remove(CLASS_NAMES.RESIZE_HANDLE_HOVER);
						previousResizerAddButtonWrappers.forEach((wrapper) => {
							wrapper.classList.remove(CLASS_NAMES.ADD_FRAGMENT_WRAPPER_VISIBLE);
						});
					}
				}
			}
		}
		//--- END handle previous item resizer ---//

		//--- BEGIN handle left add column wrapper if first column ---//
		if (column === 1) {
			const addColumnLeftWrapper = element.querySelector('.add-fragment-left-wrapper');
			if (addColumnLeftWrapper) {
				const resizeHandle = addColumnLeftWrapper.querySelector('.resize-handle');
				if (resizeHandle) {
					const isDisabled = column === 1;
					resizeHandle.classList.toggle(CLASS_NAMES.RESIZE_HANDLE_DISABLED, isDisabled);

					const leftWrapperAddButtonWrapper =
						addColumnLeftWrapper.querySelector('.add-fragment-wrapper');

					// apply or schedule hover class
					if (isInLeftThreshold) {
						const hasHoverClass = resizeHandle.classList.contains(
							CLASS_NAMES.RESIZE_HANDLE_HOVER,
						);

						// if already has class, apply immediately, otherwise delay
						if (hasHoverClass) {
							resizeHandle.classList.add(CLASS_NAMES.RESIZE_HANDLE_HOVER);
							leftWrapperAddButtonWrapper?.classList.add(
								CLASS_NAMES.ADD_FRAGMENT_WRAPPER_VISIBLE,
							);
						} else if (!showLeftWrapperTimeout) {
							showLeftWrapperTimeout = window.setTimeout(() => {
								resizeHandle.classList.add(CLASS_NAMES.RESIZE_HANDLE_HOVER);
								leftWrapperAddButtonWrapper?.classList.add(
									CLASS_NAMES.ADD_FRAGMENT_WRAPPER_VISIBLE,
								);

								showLeftWrapperTimeout = null;
							}, HOVER_SHOW_DELAY);
						}
					} else {
						// remove hover classes
						if (showLeftWrapperTimeout) {
							window.clearTimeout(showLeftWrapperTimeout);
							showLeftWrapperTimeout = null;
						}

						if (resizeHandle.classList.contains(CLASS_NAMES.RESIZE_HANDLE_HOVER)) {
							resizeHandle.classList.remove(CLASS_NAMES.RESIZE_HANDLE_HOVER);
							leftWrapperAddButtonWrapper?.classList.remove(
								CLASS_NAMES.ADD_FRAGMENT_WRAPPER_VISIBLE,
							);
						}
					}
				}
			}
		}
		//--- END handle left add column wrapper ---//
	};

	const onMouseEnter = (event: MouseEvent) => {
		handleHorizontalResizersVisibility(event);
		handleBottomActionsVisibility(event);
	};

	const onMouseMove = (event: MouseEvent) => {
		handleHorizontalResizersVisibility(event);
		handleBottomActionsVisibility(event);
	};

	const onMouseLeave = (event: MouseEvent) => {
		// Get the related target (element being entered)
		const relatedTarget = event.relatedTarget as HTMLElement;

		// Check if moving to another grid item in the same row
		const isMovingToAnotherItemInRow =
			relatedTarget &&
			(relatedTarget.classList.contains('grid-item') ||
				relatedTarget.closest('.grid-item') !== null) &&
			relatedTarget.closest(`.grid-item[data-row="${row}"]`) !== null;

		// clear all show timeouts
		if (showResizerTimeout) {
			window.clearTimeout(showResizerTimeout);
			showResizerTimeout = null;
		}
		if (showPreviousResizerTimeout) {
			window.clearTimeout(showPreviousResizerTimeout);
			showPreviousResizerTimeout = null;
		}
		if (showLeftWrapperTimeout) {
			window.clearTimeout(showLeftWrapperTimeout);
			showLeftWrapperTimeout = null;
		}
		if (showBottomActionsTimeout) {
			window.clearTimeout(showBottomActionsTimeout);
			showBottomActionsTimeout = null;
		}

		// prevent hiding if moving to another item in the same row
		if (isMovingToAnotherItemInRow) {
			return;
		}

		const hideAllResizers = () => {
			const resizer = element.querySelector('.grid-item-resizer');
			if (resizer) {
				resizer.classList.remove(CLASS_NAMES.RESIZER_HOVER_RIGHT);

				const resizeHandle = resizer.querySelector('.resize-handle');
				resizeHandle?.classList.remove(CLASS_NAMES.RESIZE_HANDLE_HOVER);

				const addButton = resizer.querySelector('.add-fragment-wrapper');
				addButton?.classList.remove(CLASS_NAMES.ADD_FRAGMENT_WRAPPER_VISIBLE);
			}

			// handle previous item's resizer if present
			const previousItem = Array.from(getRowItems()).find(
				(item) => parseInt(item.getAttribute('data-column') || '0') === column - 1,
			);
			if (previousItem) {
				const previousResizer = previousItem.querySelector('.grid-item-resizer');
				if (previousResizer) {
					previousResizer.classList.remove(CLASS_NAMES.RESIZER_HOVER_LEFT);

					const resizeHandle = previousResizer.querySelector('.resize-handle');
					resizeHandle?.classList.remove(CLASS_NAMES.RESIZE_HANDLE_HOVER);

					const addButtonWrappers =
						previousResizer.querySelectorAll('.add-fragment-wrapper');
					addButtonWrappers.forEach((wrapper) => {
						wrapper.classList.remove(CLASS_NAMES.ADD_FRAGMENT_WRAPPER_VISIBLE);
					});
				}
			}

			const leftWrapper = element.querySelector('.add-fragment-left-wrapper');
			if (leftWrapper) {
				const leftResizeHandle = leftWrapper.querySelector('.resize-handle');
				leftResizeHandle?.classList.remove(CLASS_NAMES.RESIZE_HANDLE_HOVER);

				const leftAddButton = leftWrapper.querySelector('.add-fragment-wrapper');
				leftAddButton?.classList.remove(CLASS_NAMES.ADD_FRAGMENT_WRAPPER_VISIBLE);
			}

			const firstColumnItem = Array.from(getRowItems()).find(
					(item) => item.getAttribute('data-column') === '1',
				),
				firstItemBottomActions = firstColumnItem?.querySelector(
					'.grid-item-bottom-actions',
				);

			if (firstItemBottomActions) {
				// check if popup is open
				const addButton = firstItemBottomActions.querySelector('.add-fragment-button'),
					isPopupOpen = addButton?.classList.contains(
						CLASS_NAMES.ADD_FRAGMENT_BUTTON_ACTIVE,
					);

				if (!isPopupOpen) {
					// hide bottom actions and add button wrapper if popup is not open
					firstItemBottomActions.classList.remove(CLASS_NAMES.BOTTOM_ACTIONS_HOVER);
					const addButtonWrapper = firstItemBottomActions.querySelector(
						'.add-fragment-button-wrapper',
					);
					addButtonWrapper?.classList.remove(CLASS_NAMES.ADD_FRAGMENT_WRAPPER_VISIBLE);
				}

				// always hide resize handle
				const verticalResizeHandle =
					firstItemBottomActions.querySelector('.resize-handle-vertical');
				verticalResizeHandle?.classList.remove(CLASS_NAMES.RESIZE_HANDLE_VERTICAL_HOVER);
			}
		};

		later(hideAllResizers, HOVER_HIDE_DELAY);
	};

	element.addEventListener('mouseenter', onMouseEnter);
	element.addEventListener('mousemove', onMouseMove);
	element.addEventListener('mouseleave', onMouseLeave);

	return () => {
		// clear all timeouts
		if (showResizerTimeout) {
			window.clearTimeout(showResizerTimeout);
		}

		if (showPreviousResizerTimeout) {
			window.clearTimeout(showPreviousResizerTimeout);
		}

		if (showLeftWrapperTimeout) {
			window.clearTimeout(showLeftWrapperTimeout);
		}

		if (showBottomActionsTimeout) {
			window.clearTimeout(showBottomActionsTimeout);
		}

		element.removeEventListener('mouseenter', onMouseEnter);
		element.removeEventListener('mousemove', onMouseMove);
		element.removeEventListener('mouseleave', onMouseLeave);
	};
};

const reportEditorGridItemInteractionsModifier = modifier(reportEditorGridItemInteractions);

export default reportEditorGridItemInteractionsModifier;
